import i18n from "../i18n";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * <VoiceTextarea /> — drop-in <Textarea> with a mic button that appends the user's
 * dictated speech to the value. Uses the browser's Web Speech API — 100% client-side,
 * no external service, no API key. Silently falls back to a plain <Textarea> on
 * browsers that don't support it (iOS Safari before iOS 14.5, older Firefox, etc).
 */
const VoiceTextarea = forwardRef(function VoiceTextarea(
    { value, onChange, lang = "en-US", disabled, "data-testid": testid, className = "", ...rest },
    ref,
) {
    const [listening, setListening] = useState(false);
    const [supported, setSupported] = useState(false);
    const recRef = useRef(null);

    useEffect(() => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        setSupported(!!SR);
    }, []);

    const start = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) return;
        try {
            const r = new SR();
            r.lang = lang;
            r.continuous = true;
            r.interimResults = false;
            let carry = value || "";
            r.onresult = (ev) => {
                let final = "";
                for (let i = ev.resultIndex; i < ev.results.length; i++) {
                    if (ev.results[i].isFinal) final += ev.results[i][0].transcript + " ";
                }
                if (final) {
                    const next = (carry + (carry && !carry.endsWith(" ") ? " " : "") + final).trimEnd() + " ";
                    carry = next;
                    onChange?.({ target: { value: next } });
                }
            };
            r.onerror = (ev) => {
                if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
                    toast.error("Microphone blocked. Enable mic access in your browser.");
                } else if (ev.error !== "no-speech" && ev.error !== "aborted") {
                    toast.error(`Voice error: ${ev.error}`);
                }
                setListening(false);
            };
            r.onend = () => setListening(false);
            r.start();
            recRef.current = r;
            setListening(true);
        } catch (e) {
            toast.error("Voice not available on this browser");
            setListening(false);
        }
    };
    const stop = () => {
        try { recRef.current?.stop(); } catch (e) { /* noop */ }
        setListening(false);
    };
    useEffect(() => () => stop(), []);

    return (
        <div className="relative">
            <Textarea
                ref={ref}
                value={value || ""}
                onChange={onChange}
                disabled={disabled}
                data-testid={testid}
                className={`pr-11 ${className}`}
                {...rest}
            />
            {supported && (
                <Button
                    type="button"
                    size="sm"
                    variant={listening ? "default" : "outline"}
                    className={`absolute top-1.5 right-1.5 h-8 w-8 p-0 ${listening ? "animate-pulse" : ""}`}
                    disabled={disabled}
                    onClick={listening ? stop : start}
                    title={listening ? "Stop dictating" : "Dictate with voice"}
                    aria-label={listening ? "Stop voice input" : "Start voice input"}
                    data-testid={testid ? `${testid}-mic` : "voice-mic-btn"}
                >
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
            )}
            {listening && (
                <div className="absolute bottom-1.5 right-11 text-[10px] text-primary flex items-center gap-1" data-testid="voice-listening-indicator">
                    <Loader2 className="w-3 h-3 animate-spin" />{i18n.t("final.6c19a4761d7e")}
                </div>
            )}
        </div>
    );
});

export default VoiceTextarea;
